import moment from 'moment';
import {
  ChecklistEntryTasks,
  ChecklistTasks,
  WP_Checklist,
  WP_ChecklistEntry,
} from '../model/checklist/checklist';
import { User } from '../model/models';
import { ChecklistEntryMap } from '../state/GlobalState';

export class ChecklistService {
  public static getEntry(
    checklistId: number | undefined,
    allChecklistsEntries: ChecklistEntryMap
  ): WP_ChecklistEntry | null {
    return checklistId !== undefined
      ? allChecklistsEntries['entries_' + checklistId]
      : null;
  }

  public static getChecklistTotalTasksCount = (checklist: WP_Checklist) => {
    return checklist?.acf?.checklist_tasks &&
      checklist.acf.checklist_tasks.length
      ? checklist?.acf.checklist_tasks.length
      : 0;
  };

  public static getChecklistCheckedTasksCount = (
    checklist: WP_Checklist,
    checklist_entry: WP_ChecklistEntry
  ): number => {
    if (checklist?.acf.checklist_tasks) {
      const checkedTasks = checklist?.acf.checklist_tasks.filter((task) => {
        const entry_task = ChecklistService.getEntryTask(task, checklist_entry);
        return entry_task && entry_task.entry_checklist_tasks_value;
      });
      return checkedTasks.length;
    } else {
      return 0;
    }
  };

  public static isDone = (
    checklist: WP_Checklist,
    checklist_entry: WP_ChecklistEntry
  ): boolean => {
    return (
      ChecklistService.getChecklistCheckedTasksCount(
        checklist,
        checklist_entry
      ) >= ChecklistService.getChecklistTotalTasksCount(checklist)
    );
  };

  /**
   * Return the age in days of the checklist.
   * @param user
   * @param checklist
   * @returns number of day since activation date (-1 if can't compute)
   */
  public static getAge = (
    user: User | undefined,
    checklist: WP_Checklist | null
  ): number => {
    let days: number = -1;
    if (checklist !== null && user) {
      const checklist_start_date_ref = ChecklistService.getChecklistDay(
        user,
        checklist,
        'startdate'
      );
      const valid_start_date =
        checklist.acf.checklist_start_before_after === 'after'
          ? moment(checklist_start_date_ref).add(
              parseInt(checklist.acf.checklist_start_number),
              'd'
            )
          : moment(checklist_start_date_ref).subtract(
              parseInt(checklist.acf.checklist_start_number),
              'd'
            );

      const today = moment(new Date());

      const duration = moment.duration(today.diff(valid_start_date));
      days = duration.asDays();
    }
    return days;
  };

  public static countChecklistTodo = (
    allChecklists: WP_Checklist[],
    allChecklistEntries: ChecklistEntryMap
  ): number => {
    return allChecklists.filter((c: WP_Checklist) => {
      const entry = ChecklistService.getEntry(c.id, allChecklistEntries);
      return entry && !ChecklistService.isDone(c, entry);
    }).length;
  };

  public static getEntryTask = (
    checklist_task: ChecklistTasks,
    checklist_entry: WP_ChecklistEntry | null
  ): ChecklistEntryTasks | undefined =>
    checklist_entry?.acf?.entry_checklist_tasks !== false
      ? checklist_entry?.acf?.entry_checklist_tasks.find(
          (t) =>
            t.entry_checklist_tasks_unique_id ===
            checklist_task?.checklist_tasks_key
        )
      : undefined;

  public static getChecklistDay = (
    user: User,
    checklist: WP_Checklist,
    typedate: 'enddate' | 'startdate'
  ) => {
    // For the refDay, we check inside the start or the end date the value of the acf field, example : checklist.acf.checklist_start_date can have either "start_date", "end_date" or "fixe_date"
    const refDay =
      typedate === 'enddate'
        ? checklist.acf.checklist_end_date
        : checklist.acf.checklist_start_date;

    let checklist_day_ref;
    if (refDay === 'start_date' && user?.acf.staystart !== undefined) {
      checklist_day_ref = user?.acf.staystart.substring(0, 8);
    } else if (refDay === 'end_date' && user?.acf.stayend !== undefined) {
      checklist_day_ref = user?.acf.stayend.substring(0, 8);
    } else if (refDay === 'fixe_date') {
      checklist_day_ref =
        typedate === 'enddate'
          ? moment(
              checklist.acf.checklist_end_fixe_date_choose,
              'DD/MM/YYYY'
            ).format('YYYYMMDD')
          : moment(
              checklist.acf.checklist_start_fixe_date_choose,
              'DD/MM/YYYY'
            ).format('YYYYMMDD');
    }
    return checklist_day_ref;
  };

  public static isChecklistActive = (user: User, checklist: WP_Checklist) => {
    const checklist_start_day_ref = ChecklistService.getChecklistDay(
      user,
      checklist,
      'startdate'
    );
    const checklist_end_day_ref = ChecklistService.getChecklistDay(
      user,
      checklist,
      'enddate'
    );

    if (
      checklist_start_day_ref !== undefined &&
      checklist_end_day_ref !== undefined
    ) {
      const valid_start_date =
        checklist.acf.checklist_start_before_after === 'after'
          ? moment(checklist_start_day_ref)
              .add(parseInt(checklist.acf.checklist_start_number), 'd')
              .format('YYYY-MM-DD')
          : moment(checklist_start_day_ref)
              .subtract(parseInt(checklist.acf.checklist_start_number), 'd')
              .format('YYYY-MM-DD');
      const valid_end_date =
        checklist.acf.checklist_end_before_after === 'after'
          ? moment(checklist_end_day_ref)
              .add(parseInt(checklist.acf.checklist_end_number), 'd')
              .format('YYYY-MM-DD')
          : moment(checklist_end_day_ref)
              .subtract(parseInt(checklist.acf.checklist_end_number), 'd')
              .format('YYYY-MM-DD');
      return moment().format('YYYY-MM-DD') >= valid_start_date &&
        moment().format('YYYY-MM-DD') <= valid_end_date
        ? true
        : false;
    } else {
      return false;
    }
  };

  public static checklistComparator = (
    user: User,
    a: WP_Checklist,
    b: WP_Checklist
  ) => {
    const checklist_start_day_a = ChecklistService.getChecklistDay(
      user,
      a,
      'startdate'
    );
    const checklist_start_day_b = ChecklistService.getChecklistDay(
      user,
      b,
      'startdate'
    );
    const valid_start_date_a =
      a.acf.checklist_start_before_after === 'after'
        ? moment(checklist_start_day_a)
            .add(parseInt(a.acf.checklist_start_number), 'd')
            .format('YYYYMMDD')
        : moment(checklist_start_day_a)
            .subtract(parseInt(a.acf.checklist_start_number), 'd')
            .format('YYYYMMDD');

    const valid_start_date_b =
      b.acf.checklist_start_before_after === 'after'
        ? moment(checklist_start_day_b)
            .add(parseInt(b.acf.checklist_start_number), 'd')
            .format('YYYYMMDD')
        : moment(checklist_start_day_b)
            .subtract(parseInt(b.acf.checklist_start_number), 'd')
            .format('YYYYMMDD');

    if (parseInt(valid_start_date_a) < parseInt(valid_start_date_b)) {
      return 1;
    } else if (parseInt(valid_start_date_a) > parseInt(valid_start_date_b)) {
      return -1;
    } else {
      return 0;
    }
  };
}
